import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { Board, WorkItem, WorkKind } from "../lib/types";
import { statusVisual, channelChip } from "../lib/status";
import { Tile, SectionHeader, Pill, Avatar, StatusDot } from "../components/primitives";

// Board (Kanban) with drag-drop, optimistic updates and fractional indices. A drop computes the
// (before_id, after_id) neighbours in the target column and POSTs /move; the API writes exactly
// ONE row using a fractional position (api/ordering.py). We optimistically move the card in the
// TanStack cache, then reconcile with the server response; on error we roll the cache back.

interface DragState {
  id: string;
  fromStatus: string;
}

export function BoardView({ kind = "task" }: { kind?: WorkKind }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["board", kind], queryFn: () => api.board(kind) });
  const [drag, setDrag] = useState<DragState | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: (v: { id: string; status: string; before_id: string | null; after_id: string | null }) =>
      api.moveWorkItem(v.id, { status: v.status, before_id: v.before_id, after_id: v.after_id }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["board", kind] });
      const prev = qc.getQueryData<Board>(["board", kind]);
      if (prev) {
        // Optimistic: pull the card out of its column and drop it at the top of the target.
        const card = prev.columns.flatMap((c) => c.items).find((i) => i.id === v.id);
        if (card) {
          const next: Board = {
            ...prev,
            columns: prev.columns.map((c) => ({
              ...c,
              items:
                c.status === v.status
                  ? [{ ...card, status: v.status }, ...c.items.filter((i) => i.id !== v.id)]
                  : c.items.filter((i) => i.id !== v.id),
            })),
          };
          qc.setQueryData(["board", kind], next);
        }
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["board", kind], ctx.prev); // roll back
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["board", kind] }),
  });

  const onDrop = (status: string) => (e: DragEvent) => {
    e.preventDefault();
    setOverCol(null);
    if (!drag) return;
    const col = data?.columns.find((c) => c.status === status);
    // Drop at the TOP of the target column: before_id null, after_id = current first card (if any
    // and not the dragged one). The API turns that into a fractional position.
    const first = col?.items.find((i) => i.id !== drag.id) ?? null;
    move.mutate({ id: drag.id, status, before_id: null, after_id: first?.id ?? null });
    setDrag(null);
  };

  if (isLoading) return <Tile>Loading board…</Tile>;
  if (!data) return <Tile>No board data.</Tile>;

  return (
    <Tile>
      <SectionHeader title={kind === "ticket" ? "Tickets board" : "Board"}>
        <Pill className="pill-gold">Live</Pill>
      </SectionHeader>
      <div style={{ height: 12 }} />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${data.columns.length}, 1fr)`, gap: 14, alignItems: "start" }}>
        {data.columns.map((col) => {
          const sv = statusVisual(col.status, col.label, col.is_terminal);
          const isOver = overCol === col.status;
          return (
            <div
              key={col.status}
              onDragOver={(e) => {
                e.preventDefault();
                setOverCol(col.status);
              }}
              onDragLeave={() => setOverCol((s) => (s === col.status ? null : s))}
              onDrop={onDrop(col.status)}
              style={{
                borderRadius: 12,
                padding: 4,
                outline: isOver ? "2px dashed var(--gold)" : "none",
                outlineOffset: 2,
                minHeight: 60,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 11, padding: "0 3px" }}>
                <StatusDot color={sv.color} label={col.label} />
                <span style={{ fontSize: 11, color: "var(--text-4)", fontWeight: 600, marginLeft: "auto" }}>
                  {col.items.length}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {col.items.map((it) => (
                  <BoardCard key={it.id} item={it} onDragStart={() => setDrag({ id: it.id, fromStatus: col.status })} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Tile>
  );
}

function BoardCard({ item, onDragStart }: { item: WorkItem; onDragStart: () => void }) {
  const chip = channelChip(item.source);
  const slaLabel =
    item.sla_breached_at || (item.sla_due_at && new Date(item.sla_due_at).getTime() < Date.now())
      ? "SLA breached"
      : null;
  return (
    <Link
      to={`/item/${item.id}`}
      draggable
      onDragStart={onDragStart}
      className="card"
      style={{ padding: 12, display: "block", color: "inherit", textDecoration: "none", cursor: "grab" }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-4)", letterSpacing: 0.5 }}>{item.ref}</div>
      <div style={{ fontSize: 12.8, fontWeight: 600, lineHeight: 1.4, margin: "5px 0 9px" }}>{item.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        {chip && <span className={`chip ${chip.cls}`}>{chip.label}</span>}
        {slaLabel && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--red)" }}>⚑ {slaLabel}</span>}
        {item.assignee_id && <Avatar name={item.assignee_id.slice(0, 2)} />}
      </div>
    </Link>
  );
}
